import { Injectable } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Session } from '../models/session.model';
import { UrlHelperService } from '../services/url-helper.service';
import { PermissionReceiverService } from '../services/permission-receiver.service';
import { Destroy } from '../interfaces/destroy.interface';
import { EntityService } from '@shared/services/entity.service';
import { Cart } from '../interfaces/cart.interface';
import { SecurityRole } from '../../shared/literals/shared.literal';
import { Services } from '../../shared/literals/shared.literal';
import { BooleanResult } from '../interfaces/boolean-result.interface';
import { CartService } from '../../shared/services/cart.service';
/**
 *
 *  The Authentication Service will handle logging in the user as well as being able to check if the user's token is still active
 * 
 * */

@Injectable()
export class AuthenticationService implements Destroy {

  authSubscription: Subject<boolean> = new Subject<boolean>();

  constructor(private http: HttpClient,
              private router: Router,
              private url: UrlHelperService,
              private cartService: CartService,
              private permissions: PermissionReceiverService,
              private entityService: EntityService<any>) { }

  login(username: string, password: string): Observable<any> {

    var postData = Object.freeze({ username, password });

    const options: Object = {
      responseType: 'text'
    }

    var baseUrl: string = this.url.getApiUrl();
    var url: string = baseUrl + "RedeploymentUsers/authenticate";

    return this.http.post<any>(url, postData, options).pipe(takeUntil(this.authSubscription));
  }

  sendPasswordResetEmail(email: string): Observable<BooleanResult> {
    var baseUrl: string = this.url.getApiUrl();
    var url: string = baseUrl + "RedeploymentUsers/SendForgotPasswordEmail/?email=" + email;
    return this.http.post<any>(url, {});
  }




  resetPasswordEmail(authToken: string, password: string, confirmPassword: string): Observable<BooleanResult> {
    var postData: any = {};
    postData['authToken'] = authToken;
    postData['password'] = password;
    postData['confirmPassword'] = confirmPassword;

    var url = this.url.getApiUrl() + "RedeploymentUsers/ResetPassword";
    return this.http.post<BooleanResult>(url, postData);
  }

  askForHelp(request: any): Observable<BooleanResult> {
    var url: string = this.url.getApiUrl() + "RedeploymentUsers/AskForHelp";
    return this.http.post<BooleanResult>(url, request);
  }

  public setSession(authResult: any) {

    if (authResult) {
      var decodedToken = this.decodeToken(authResult);
      var session = new Session();

      session.authToken = authResult;
      session.userId = decodedToken.ID;
      session.expireAt = new Date(decodedToken.exp * 1000);
      session.name = decodedToken.Name;
      session.username = decodedToken.Username;
      session.networkUsername = decodedToken.NetworkUsername;
      session.needsToChangePassword = decodedToken.NeedsToChangePassword;
      session.isClientRole = decodedToken.IsClientRole;
      session.organizationId = decodedToken.OrganizationId;
      session.organizationName = decodedToken.OrganizationName;
      session.securityRoleId = decodedToken.SecurityRoleId;
      session.securityRoleName = decodedToken.securityRoleName;

      var organizations = JSON.parse(decodedToken.Organizations);
      session.organizationList = organizations;

      var countries = JSON.parse(decodedToken.UserCountries);
      session.countryList = countries;

      localStorage.setItem('user_session', JSON.stringify(session));
    
      this.permissions.get();
      this.entityService.empty();

      //If the user logging in is an administration 
      if (Number(session.securityRoleId) == Number(SecurityRole.Administrator)) {
        //Navigate to another screen that will ask the admin which
        this.router.navigate(['/AdminOrganizationLogin']);

      } else {
        //just a general user!
        if (session.organizationList.length == 1) {
          this.setOrganization(session.organizationList[0].ID, session.organizationList[0].Name);
        }
      }
    } else {
      localStorage.setItem('user_session', JSON.stringify(authResult));
    }
  }

  private decodeToken(token: string) : any {
    const _dc = (token: string) => {
      try {
        return JSON.parse(window.atob(token));
      } catch {
        return;
      }
    };
    return token.split('.').map(token => _dc(token)).reduce((acc, curr) => {
      if (!!curr) acc = { ...acc, ...curr };
      return acc;
    }, Object.create(null));
  }

  public getSession(): Session | null {

    var session: Session | null = null;
    var json = localStorage.getItem('user_session');
    if (json) {
      session = JSON.parse(json.toString());
    }

    return session;
  }

  public isLoggedIn(): boolean {

    var session = this.getSession();
    if (session) {
      var now = new Date();
      var utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
      return utc < this.getExpiration();
    }

    return false;
  }

  getAuthToken(): string | null {

    var session = this.getSession();
    if (session) {
      return session.authToken;
    }

    return "";
  }

  private getExpiration(): Date {

    var expiration: Date = new Date();
    var session = this.getSession();
    if (session) {
      expiration = new Date(session.expireAt);
    }

    return expiration;
  }

  private getPermissionsLocal() : number[] {

    var session = this.getSession();
    if (session) {
      return session.permissions;
    }

    return new Array<number>;
  }

  public isAdmin(): boolean {

    var session = this.getSession();

    if (Number(session?.securityRoleId) == Number(SecurityRole.Administrator)) {
      return true;
    } else {
      return false;
    }

  }

  public isClientAdmin(): boolean {

    var session = this.getSession();

    if (Number(session?.securityRoleId) == Number(SecurityRole.ClientAdministrator)) {
      return true;
    } else {
      return false;
    }

  }

  public canPerformAction(permission: number): boolean {
    var permissions = this.getPermissionsLocal();

    if (permission != undefined) {
      if (permissions.includes(permission)) {
        return true;
      }

    }

    return false;
  }

  public canPerformActionByOrganization(organizationId: number[] | undefined): boolean {
    var session = this.getSession();

    var organization = session?.organizationId;

    if (organizationId != undefined) {
      if (organizationId.includes(Number(organization))) {
        return true;
      }

    }

    return false;
  }

  public canPerformActionByService(service: Services | undefined): boolean {
    var session = this.getSession();

    var organizationList = session?.organizationList;
    var organizationId = session?.organizationId;

    var organization = organizationList?.find(org => org.ID == organizationId);

    //If the service is undefined it means that anyone has access to it!
    //It will be protected by permission but not by organization
    var canViewPage = true;

    if (service != undefined) {
      if (Services[service] == Services[Services.GenerateBenchTemplate] && organization.GenerateBenchTemplate != true) {
        canViewPage = false;
      }
      if (Services[service] == Services[Services.ShippingMethodRequired] && organization.ShippingMethodRequired != true) {
        canViewPage = false;
      }
    }

    return canViewPage;
  }

  clearApiUrl(): void {
    localStorage.removeItem("redeployment_apiUrl");
  }

  public logout() {
    this.setSession(null);
    this.entityService.empty();

    this.router.navigate(['/Login'])
      .then(() => {
        window.location.reload();
      });
  }

  public switchOrganization(organizationId: number, organizationName: string) {
    //Probably need to completely wipe out all the reserved items...this will ONLY be for admins
    this.cartService.clearCartItems(organizationId).subscribe((data => {
      this.setOrganization(organizationId, organizationName);

      var baseUrl = window.location.href.replace(this.router.url, '');

      if (this.router.url == "/") {
        window.location.reload();
      } else {
        window.location.replace(baseUrl);
      }
    }));

  }

  public getOrganizationId(): number {
    var session = this.getSession();
    if (session) {
      return session.organizationId;
    }

    throw "Something went wrong!";
  }

  public getOrganizationName(): string {
    var session = this.getSession();
    if (session) {
      return session.organizationName;
    }

    throw "Something went wrong!";
  }

  public getSecurityRoleInfo(): any {
    var session = this.getSession();
    if (session) {
      return { id: session.securityRoleId, name: session.securityRoleName };
    }

    return null;
  }

  public setOrganization(organizationId: number, organizationName: string): void {
    var session = this.getSession();
    if (session && organizationId && organizationName) {
      var organization = session.organizationList.find(x => x.ID == organizationId);

      session.organizationId = organizationId;
      session.organizationName = organizationName;

      session.generateBenchTemplate = organization.GenerateBenchTemplate;
      session.shippingMethodRequired = organization.ShippingMethodRequired;
      session.showHdrInfo = organization.ShowHdrInfo;

      localStorage.setItem('user_session', JSON.stringify(session));
    }
  }

  public setForceChangePasword(forceChangePasword: boolean, authUserId: number) {
    var session = this.getSession();
    //Only set the session if the current user needs to change their password
    if (session && forceChangePasword != null && authUserId == session.userId) {
      session.needsToChangePassword = String(forceChangePasword);

      localStorage.setItem('user_session', JSON.stringify(session));
    }
  }

  //public getSignalrConnectionId(): string {
  //  var value = localStorage.getItem("redeployment_signalr_connectionId");
  //  if (value) {
  //    return value.toString();
  //  }
  //  return "";
  //}

  onDestroy(): void {
    this.authSubscription.next(true);
    this.authSubscription.unsubscribe();
  }


  canView(permission: number): boolean {
    if (this && this.canPerformAction(permission)) {
      return true;
    } else {
      return false;
    }
  }

}
